import React from "react";
import { ActivityIndicator, ListRenderItem, StyleSheet, View } from "react-native";

import { tColumn, tColumnId, tRow, tRowInstance, useTable } from "src/hooks/table";
import { PdTable, PdTableCell, PdTableRow, PdTableSortableHeaders } from "src/components/v2/layout/table";
import { PdCopy, PdSettings } from "src/components/Icons";
import { kStyleConst } from "src/kStyle";
import { kColors } from "src/kColors";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { useAppDispatch } from "src/redux";
import { PdRow } from "src/components/v2/ui/core";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";
import { tTables } from "src/redux/TableConfig/types";
import { useIsMobile } from "src/hooks/ui";

type tReportTable<T> = {
  data: tRow<T>[];
  columns: tColumn<T>[];
  sortedBy: tColumnId;
  columnsConfig: Record<tColumnId, boolean>;
  tableConfigReduxKey: tTables;
  keyExtractor: (item: tRowInstance<T>, index: number) => string;
};

export const ReportTable = <T extends unknown>({
  data,
  columns,
  sortedBy,
  keyExtractor,
  tableConfigReduxKey,
  columnsConfig,
}: tReportTable<T>): React.ReactElement => {
  const isMobile = useIsMobile();
  const tableInstance = useTable<T>({
    data,
    columns,
    initialState: {
      sortedBy,
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(columnsConfig),
    },
  });
  const dispatch = useAppDispatch();

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Partial<Record<tColumnId, boolean>>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const renderItem: ListRenderItem<tRowInstance<T>> = ({ item }) => {
    return (
      <PdTableRow>
        {item.cells.map((cell) => (
          <PdTableCell key={cell.uniqueId} style={cell.style}>
            {cell.render("Cell")}
          </PdTableCell>
        ))}
      </PdTableRow>
    );
  };

  return (
    <>
      {!isMobile && (
        <PdRow style={styles.reportTableButtons}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey={tableConfigReduxKey}
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdRow>
      )}
      <PdTable
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        data={tableInstance.rows}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
      />
    </>
  );
};

export const PdSpinner: React.FC = () => (
  <View style={styles.pdSpinner}>
    <ActivityIndicator size="large" color={kColors.brandGreen} />
  </View>
);

const styles = StyleSheet.create({
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  pdSpinner: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  reportTableButtons: {
    gap: kStyleConst.kSpacing * 2,
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
});
